/**
 * msAi 前端应用主组件
 */

import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import zhCN from "antd/locale/zh_CN";
import { MainLayout } from "@ms/shared-components";
import { AuthGuard } from "@ms/shared-auth";
import "./config/auth";
import {
  DatabaseOutlined,
  FileTextOutlined,
  ThunderboltOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { MENU_ROUTE_CONFIG, ROUTE_CONFIG } from "./config/routeConfig";

// 导入页面
import ResourcePage from "./pages/resource";
import LogsPage from "./pages/logs";
import ServiceDiscoveryPage from "./pages/service-discovery";
import ComponentPage from "./pages/component";
import DatasetPage from "./pages/dataset";
import ThinkingModePage from "./pages/thinking-mode";
import ResponseFormatPage from "./pages/response-format";
import UnifiedManagementPage from "./pages/unified-management";

/**
 * 应用内容组件（在 Router 内部）
 */
const AppContent: React.FC = () => {
  const navigate = useNavigate();

  // 菜单配置（包含图标和点击事件，支持子菜单）
  const menuItems = React.useMemo(() => {
    const getIcon = (iconName?: string) => {
      switch (iconName) {
        case "DatabaseOutlined":
          return <DatabaseOutlined />;
        case "ThunderboltOutlined":
          return <ThunderboltOutlined />;
        case "AppstoreOutlined":
          return <AppstoreOutlined />;
        case "FileTextOutlined":
          return <FileTextOutlined />;
        default:
          return <FileTextOutlined />;
      }
    };

    const convertMenuItem = (item: any): any => {
      const menuItem: any = {
        key: item.key,
        label: item.label,
        icon: getIcon(item.icon),
      };

      // 如果有子菜单，递归处理
      if (item.children && item.children.length > 0) {
        menuItem.children = item.children.map((child: any) => ({
          key: child.key,
          label: child.label,
          icon: getIcon(child.icon),
          path: child.path,
          onClick: () => {
            navigate(child.path);
          },
        }));
      } else if (item.path) {
        // 只有叶子节点才有点击事件
        menuItem.path = item.path;
        menuItem.onClick = () => {
          navigate(item.path);
        };
      }

      return menuItem;
    };

    return MENU_ROUTE_CONFIG.map(convertMenuItem);
  }, [navigate]);

  // 布局配置
  const layoutConfig = React.useMemo(
    () => ({
      system: {
        name: "AI 资源管理",
        shortName: "msAi",
      },
      menuItems,
    }),
    [menuItems],
  );

  return (
    <MainLayout config={layoutConfig}>
      <Routes>
        <Route path={ROUTE_CONFIG.RESOURCE} element={<ResourcePage />} />
        <Route
          path={ROUTE_CONFIG.AI_CONFIG_COMPONENT}
          element={<ComponentPage />}
        />
        <Route
          path={ROUTE_CONFIG.AI_CONFIG_DATASET}
          element={<DatasetPage />}
        />
        <Route
          path={ROUTE_CONFIG.AI_CONFIG_THINKING_MODE}
          element={<ThinkingModePage />}
        />
        <Route
          path={ROUTE_CONFIG.AI_CONFIG_RESPONSE_FORMAT}
          element={<ResponseFormatPage />}
        />
        {/* 兼容旧路由 */}
        <Route
          path={ROUTE_CONFIG.AI_CONFIG}
          element={<Navigate to={ROUTE_CONFIG.AI_CONFIG_COMPONENT} replace />}
        />
        <Route path={ROUTE_CONFIG.LOGS} element={<LogsPage />} />
        <Route
          path={ROUTE_CONFIG.SERVICE_DISCOVERY}
          element={<ServiceDiscoveryPage />}
        />
        <Route
          path={ROUTE_CONFIG.UNIFIED_MANAGEMENT}
          element={<UnifiedManagementPage />}
        />
        <Route
          path="/"
          element={<Navigate to={ROUTE_CONFIG.RESOURCE} replace />}
        />
      </Routes>
    </MainLayout>
  );
};

/**
 * 主应用组件
 */
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ConfigProvider locale={zhCN}>
        <AuthGuard>
          <AppContent />
        </AuthGuard>
      </ConfigProvider>
    </BrowserRouter>
  );
};

export default App;
